import { loadRules, assess, requiredLevel } from '../password-strength.js';

/**
 * <wdc-password-meter for="new_password"> — how strong the password in that
 * field of the same form is, as it is typed, and what would make it
 * stronger. On every form that sets a password (chunk 3.7).
 *
 *   meter.context = [uid, firstName, lastName];  // words it must not contain
 *   await meter.check()                          // null if it may be saved,
 *                                                // else the reason, a sentence
 *
 * The API applies the same rules (\system\auth\PasswordStrength), so this
 * saves a round trip and explains; it is not the gate. If the rules cannot
 * be fetched, check() says nothing and the server decides.
 */
const HINTS = {
  short: (p) => `Use at least ${p.min_length} characters.`,
  common: () => 'This is one of the most common passwords.',
  name: () => 'Leave out the name and username.',
  'common-word': () => 'A common word with a few characters added — add more of your own.',
  weak: () => 'Make it longer, or mix in capitals, numbers or symbols.',
};

const LABELS = {
  short: 'Too short', common: 'Too common', name: 'Contains a name',
  weak: 'Weak', fair: 'Fair', good: 'Good', strong: 'Strong',
};

class WdcPasswordMeter extends HTMLElement {
  #input = null;
  #context = [];
  #rules = null;
  #onInput = () => this.#update();

  set context(words) {
    this.#context = words.filter(Boolean);
    this.#update();
  }

  connectedCallback() {
    this.#input = this.closest('form')?.elements.namedItem(this.getAttribute('for')) ?? null;
    this.innerHTML = `
      <div class="pw-meter-bar" data-level=""><span></span><span></span><span></span><span></span></div>
      <div class="pw-meter-text"><strong data-m="label"></strong> <span data-m="hint"></span></div>
      <div class="pw-meter-note" data-m="note"></div>`;
    this.#input?.addEventListener('input', this.#onInput);
    this.#load();
  }

  disconnectedCallback() {
    this.#input?.removeEventListener('input', this.#onInput);
  }

  /** null if the password may be saved; otherwise why not, as a sentence. */
  async check() {
    try { await this.#load(); } catch { return null; }
    const r = this.#assess();
    if (r.level >= requiredLevel(this.#rules.policy)) return null;
    return r.level === 0 || r.reason === 'common-word'
      ? HINTS[r.reason](this.#rules.policy)
      : `That password is only ${r.label}. ${HINTS.weak()}`;
  }

  async #load() {
    if (this.#rules) return;
    try {
      this.#rules = await loadRules();
    } catch {
      this.querySelector('[data-m="note"]').textContent = '';
      throw new Error('Could not load the password rules');
    }
    const p = this.#rules.policy;
    this.querySelector('[data-m="note"]').textContent =
      `At least ${p.min_length} characters — ${p.recommended_length} or more is recommended.`;
    this.#update();
  }

  #assess() {
    return assess(this.#input?.value ?? '', this.#context, this.#rules);
  }

  #update() {
    if (!this.#rules || !this.isConnected) return;
    const empty = !(this.#input?.value);
    const r = this.#assess();
    const key = r.level === 0 ? r.reason : r.label;
    this.querySelector('.pw-meter-bar').dataset.level = empty ? '' : String(r.level);
    this.querySelector('[data-m="label"]').textContent = empty ? '' : LABELS[key];
    this.querySelector('[data-m="hint"]').textContent =
      empty || r.reason === null ? '' : HINTS[r.reason](this.#rules.policy);
  }
}

customElements.define('wdc-password-meter', WdcPasswordMeter);
